import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RefreshCw, ExternalLink, Mail, Phone, User } from 'lucide-react';
import { apiService, CustomFieldDefinition, CustomFieldValue } from '../services/api';
import { usePhantomJobStatus } from '../contexts/PhantomJobContext';
import { FieldDisplay } from './DataTable/fields';
import { useTableContextOrNull } from './DataTable/contexts';
import { MultiselectEditor, DateEditor, PersonEditor } from './DataTable/fields/editors';

interface DisplayFounder {
  id: string;
  name: string;
  email: string;
  company: string;
  companyInitial: string;
  companyColor: string;
  stage: string;
  teamMember: string;
  teamMemberInitials?: string;
  teamMemberColor?: string;
  lastPostSummary?: string;
  lastPostSummaryDate?: string; // When the LinkedIn summary was last updated
  lastPostDate?: string; // When the actual LinkedIn post was published
  lastPostExcerpt?: string; // Excerpt from the latest post
  nextAction?: string; // Next action for this founder
  customFieldValues?: CustomFieldValue[];
  industry?: string;
  location?: string;
  followerCount?: number;
  priorityScore?: number;
  status?: string;
  tag?: string;
  owner?: string;
  lastTouchAt?: string;
  tabId?: string;
  profileUrl?: string;
  roleTitle?: string;
  selected?: boolean;
}

interface TableRowProps {
  founder: DisplayFounder;
  visibleColumns: string[];
  columnWidths: Record<string, number>;
  customFields: CustomFieldDefinition[];
  onFounderClick?: (founderId: string) => void;
  onRefresh?: () => void;
  onToggleSelect?: (founderId: string, checked: boolean) => void;
  onDeleteFounder?: (founderId: string) => void;
  onFounderUpdate?: (founderId: string, updates: Partial<DisplayFounder>) => void;
  getInitials: (name: string) => string;
  formatUpdateTime: (dateString: string) => string;
  rowNumber?: number;
  rowIndex?: number;
  activeCell?: { row: number; colKey: string } | null;
  selectionRect?: { r0: number; c0: number; r1: number; c1: number; colOrder: string[] } | null;
  onCellClick?: (row: number, colKey: string) => void;
  onCellMouseDown?: (row: number, colKey: string) => void;
  onCellMouseMove?: (row: number, colKey: string) => void;
  shouldStartEditing?: { row: number; colKey: string; initialChar?: string } | null;
  setIsEditing?: (isEditing: boolean) => void;
  isTableLocked?: boolean;
}

const TableRow: React.FC<TableRowProps> = ({
  founder,
  visibleColumns,
  columnWidths,
  customFields,
  onFounderClick,
  onRefresh,
  onToggleSelect,
  onDeleteFounder,
  onFounderUpdate,
  getInitials,
  formatUpdateTime,
  rowNumber,
  rowIndex,
  activeCell,
  selectionRect,
  onCellClick,
  onCellMouseDown,
  onCellMouseMove,
  shouldStartEditing,
  setIsEditing,
  isTableLocked
}) => {
  const { hasActivePhantomJobs } = usePhantomJobStatus();

  // Use context when available, fall back to props
  const tableContext = useTableContextOrNull();
  const effectiveCustomFields = tableContext?.customFields ?? customFields;
  const effectiveActiveCell = tableContext?.activeCell ?? activeCell;
  const effectiveSelectionRect = tableContext?.selectionRect ?? selectionRect;
  const effectiveShouldStartEditing = tableContext?.shouldStartEditing ?? shouldStartEditing;
  const effectiveIsTableLocked = tableContext?.isTableLocked ?? isTableLocked;

  const [editingCell, setEditingCell] = useState<{founderId: string, field: 'name' | 'email' | 'company' | 'linkedinUrl' | 'industry' | 'location' | 'stage' | 'position' | 'status' | 'tag' | 'accelerator' | 'contactPhone' | 'lastEditedBy' | 'type' | 'vcFirm' | 'xUrl' | 'substackUrl'} | null>(null);
  const [editingCustomField, setEditingCustomField] = useState<{founderId: string, fieldId: string} | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [editorPosition, setEditorPosition] = useState<{top: number, left: number} | null>(null);
  const editingInputRef = useRef<HTMLInputElement | null>(null);
  const [latestAISummary, setLatestAISummary] = useState<string | null>(null);
  const [latestPostTimestamp, setLatestPostTimestamp] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);
  const [loadingAISummary, setLoadingAISummary] = useState<boolean>(false);
  const [refreshingSummary, setRefreshingSummary] = useState<boolean>(false);
  const [showRefreshModal, setShowRefreshModal] = useState<boolean>(false);

  // Notify parent when editing state changes
  useEffect(() => {
    if (setIsEditing) {
      // Defer state update to avoid React error #185 (updating during render)
      setTimeout(() => {
        setIsEditing(editingCell !== null);
      }, 0);
    }
  }, [editingCell, setIsEditing]);
  
  // Fetch latest AI summary when component mounts
  useEffect(() => {
    const fetchAISummary = async () => {
      // Skip fetching for new/temporary rows (IDs starting with "new-")
      if (!founder.id || founder.id.startsWith('new-')) {
        return;
      }
      
      setLoadingAISummary(true);
      try {
        const response = await apiService.getLatestAISummary(founder.id);
        setLatestAISummary(response.latestAISummary);
        setLatestPostTimestamp(response.latestPostTimestamp);
        setLastUpdatedAt(response.lastUpdatedAt);
      } catch (error) {
        console.error('Error fetching AI summary:', error);
        setLatestAISummary(null);
        setLatestPostTimestamp(null);
        setLastUpdatedAt(null);
      } finally {
        setLoadingAISummary(false);
      }
    };

    fetchAISummary();
  }, [founder.id]);

  // Start editing when shouldStartEditing is set for this row's cell
  useEffect(() => {
    if (!effectiveShouldStartEditing || effectiveShouldStartEditing.row !== (rowIndex ?? -1) || !effectiveShouldStartEditing.colKey) return;
    // Prevent editing if table is locked
    if (effectiveIsTableLocked) return;
    // Don't re-trigger if already editing
    if (editingCell) return;

    const activeCell = effectiveShouldStartEditing;

    const colKeyToField: Record<string, 'name' | 'email' | 'company' | 'linkedinUrl' | 'industry' | 'location' | 'stage' | 'position' | 'status' | 'tag' | 'accelerator' | 'contactPhone' | 'lastEditedBy' | 'type' | 'vcFirm' | 'xUrl' | 'substackUrl'> = {
      name: 'name',
      email: 'email',
      company: 'company',
      position: 'position',
      linkedinUrl: 'linkedinUrl',
      industry: 'industry',
      location: 'location',
      stage: 'stage',
      status: 'status',
      tag: 'tag',
      accelerator: 'accelerator',
      contactPhone: 'contactPhone',
      lastEditedBy: 'lastEditedBy',
      type: 'type',
      vcFirm: 'vcFirm',
      xUrl: 'xUrl',
      substackUrl: 'substackUrl',
    };

    const field = colKeyToField[activeCell.colKey];
    if (field) {
      // Get current value based on field
      let currentValue = '';
      if (field === 'name') currentValue = founder.name || '';
      else if (field === 'email') currentValue = founder.email || '';
      else if (field === 'company') currentValue = founder.company || '';
      else if (field === 'position') currentValue = founder.roleTitle || '';
      else if (field === 'linkedinUrl') currentValue = founder.profileUrl || '';
      else if (field === 'industry') currentValue = founder.industry || '';
      else if (field === 'location') currentValue = founder.location || '';
      else if (field === 'stage') currentValue = founder.stage || '';
      else if (field === 'status') currentValue = founder.status || '';
      else if (field === 'tag') currentValue = founder.tag || '';
      
      console.log('[TableRow] Starting edit for activeCell:', { field, currentValue, initialChar: activeCell.initialChar });
      // Defer state updates to avoid React error #185 (updating during render)
      setTimeout(() => {
        setEditingCell({ founderId: founder.id, field });
        // If initialChar is provided, start with that instead of current value
        setEditingValue(activeCell.initialChar || currentValue);
        setOriginalEditValue(currentValue);
      }, 0);
    } else {
      // Check if it's a custom field ID
      const customField = effectiveCustomFields.find(cf => cf.id === activeCell.colKey);
      if (customField) {
        const currentValue = getCustomFieldValue(founder, activeCell.colKey);
        console.log('[TableRow] Starting custom field edit:', { fieldId: activeCell.colKey, currentValue, initialChar: activeCell.initialChar });
        setTimeout(() => {
          // Find the cell element to get its position for portal-based editors
          const cellElement = document.querySelector(`tr:nth-child(${(rowIndex ?? 0) + 1}) td.custom-field-column`) as HTMLElement;
          if (cellElement) {
            const rect = cellElement.getBoundingClientRect();
            setEditorPosition({ top: rect.bottom, left: rect.left });
          }
          setEditingCustomField({ founderId: founder.id, fieldId: activeCell.colKey });
          setEditingValue(activeCell.initialChar || currentValue);
        }, 0);
      }
    }
  }, [effectiveShouldStartEditing, rowIndex, founder, editingCell, effectiveCustomFields, effectiveIsTableLocked]);

  // Position cursor at end when editing starts (unless typing initial character)
  useEffect(() => {
    if ((editingCell || editingCustomField) && editingInputRef.current && !effectiveShouldStartEditing?.initialChar) {
      // Small delay to ensure input is rendered
      setTimeout(() => {
        if (editingInputRef.current) {
          const input = editingInputRef.current;
          const len = input.value.length;
          // Focus first to ensure input is ready
          input.focus();
          // Set cursor position to the end (after the last character)
          input.setSelectionRange(len, len);
          // Ensure cursor is at the end even after focus
          requestAnimationFrame(() => {
            if (editingInputRef.current) {
              const input = editingInputRef.current;
              const len = input.value.length;
              input.setSelectionRange(len, len);
              // One more attempt after a micro-delay to ensure it sticks
              setTimeout(() => {
                if (editingInputRef.current) {
                  const input = editingInputRef.current;
                  const len = input.value.length;
                  input.setSelectionRange(len, len);
                }
              }, 0);
            }
          });
        }
      }, 10);
    }
  }, [editingCell, editingCustomField, effectiveShouldStartEditing]);

  // Show refresh confirmation modal
  const handleRefreshClick = () => {
    setShowRefreshModal(true);
  };

  // Confirm and execute refresh
  const handleConfirmRefresh = async () => {
    setShowRefreshModal(false);
    if (refreshingSummary) return; // Prevent multiple simultaneous refreshes
    
    setRefreshingSummary(true);
    try {
      console.log('🔄 Refreshing AI summary for founder:', founder.id);
      const result = await apiService.refreshAISummaries(founder.id);
      
      if (result.success) {
        console.log(`✅ Refresh successful: ${result.newPostsCount} new posts, ${result.updatedSummariesCount} summaries updated`);
        
        // Refetch the latest AI summary to update the display
        const summaryData = await apiService.getLatestAISummary(founder.id);
        setLatestAISummary(summaryData.latestAISummary);
        setLatestPostTimestamp(summaryData.latestPostTimestamp);
        setLastUpdatedAt(summaryData.lastUpdatedAt);
        
        // Call onRefresh to update the parent component
        if (onRefresh) {
          onRefresh();
        }
      } else {
        console.error('❌ Refresh failed:', result.error);
        alert(`Failed to refresh AI summary: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Error refreshing AI summary:', error);
      alert('Failed to refresh AI summary. Please try again.');
    } finally {
      setRefreshingSummary(false);
    }
  };

  // Cancel refresh
  const handleCancelRefresh = () => {
    setShowRefreshModal(false);
  };

  // Handle LinkedIn posts button click - go directly to most recent post
  const handlePostsClick = async () => {
    try {
      // Fetch the most recent LinkedIn post URL
      const posts = await apiService.getLinkedinPosts(founder.id);
      if (posts && posts.length > 0) {
        // Open the most recent post in a new tab
        window.open(posts[0].postUrl, '_blank', 'noopener,noreferrer');
      } else {
        // If no posts found, try to open the founder's LinkedIn profile
        if (founder.profileUrl) {
          window.open(founder.profileUrl, '_blank', 'noopener,noreferrer');
        } else {
          alert('No LinkedIn posts or profile URL found for this founder.');
        }
      }
    } catch (error) {
      console.error('Error fetching LinkedIn posts:', error);
      // Fallback to profile URL if available
      if (founder.profileUrl) {
        window.open(founder.profileUrl, '_blank', 'noopener,noreferrer');
      } else {
        alert('Unable to open LinkedIn content for this founder.');
      }
    }
  };

  const [originalEditValue, setOriginalEditValue] = useState<string>('');

  // Helper function to format last refreshed time in a human-friendly way
  const formatLastRefreshed = (dateString?: string): string => {
    if (!dateString) return 'Never refreshed';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) { // 24 hours
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else if (diffInMinutes < 10080) { // 7 days
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    } else {
      // For older dates, show the actual date
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Helper function to format post date in a human-friendly way
  const formatPostDate = (dateString?: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    
    // For posts older than 7 days, show the actual date
    if (diffInDays >= 7) {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    } else if (diffInDays >= 1) {
      return `${diffInDays}d ago`;
    } else {
      // For today's posts, show the time
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    }
  };

  const handleCellDoubleClick = (founderId: string, field: 'name' | 'email' | 'company' | 'linkedinUrl' | 'industry' | 'location' | 'stage' | 'position' | 'status' | 'tag' | 'accelerator' | 'contactPhone' | 'lastEditedBy' | 'type' | 'vcFirm' | 'xUrl' | 'substackUrl', currentValue: string) => {
    // Prevent editing if table is locked
    if (effectiveIsTableLocked) {
      return;
    }
    setEditingCell({ founderId, field });
    setEditingValue(currentValue);
    setOriginalEditValue(currentValue);
  };

  const handleSaveEdit = async () => {
    if (!editingCell) return;

    // If value hasn't changed, just close the editor
    if (editingValue === originalEditValue) {
      setEditingCell(null);
      setEditingValue('');
      setOriginalEditValue('');
      return;
    }

    try {
      const updates: Partial<{ name?: string; email?: string; company?: string; profileUrl?: string; industry?: string; location?: string; stage?: string; status?: string; tag?: string; roleTitle?: string; accelerator?: string; contactPhone?: string; lastEditedBy?: string; type?: string; vcFirm?: string; xUrl?: string; substackUrl?: string }> = {};
      if (editingCell.field === 'name') {
        updates.name = editingValue.trim();
      } else if (editingCell.field === 'linkedinUrl') {
        updates.profileUrl = editingValue;
      } else if (editingCell.field === 'position') {
        updates.roleTitle = editingValue.trim();
      } else if (editingCell.field === 'industry' || editingCell.field === 'location' || editingCell.field === 'stage' || editingCell.field === 'status' || editingCell.field === 'tag' || editingCell.field === 'accelerator' || editingCell.field === 'contactPhone' || editingCell.field === 'lastEditedBy' || editingCell.field === 'type' || editingCell.field === 'vcFirm' || editingCell.field === 'xUrl' || editingCell.field === 'substackUrl') {
        updates[editingCell.field] = editingValue;
      } else {
        updates[editingCell.field] = editingValue;
      }
      
      // Check if this is a new row (ID starts with 'new-')
      const isNewRow = editingCell.founderId.startsWith('new-');
      
      if (isNewRow) {
        // For new rows, just call onFounderUpdate which will handle creation
        if (onFounderUpdate) {
          onFounderUpdate(editingCell.founderId, updates as Partial<DisplayFounder>);
        }
      } else {
        // For existing rows, update via API
        await apiService.updateFounder(editingCell.founderId, updates);
        
        // Optimistically update the UI immediately
        if (onFounderUpdate) {
          onFounderUpdate(editingCell.founderId, updates as Partial<DisplayFounder>);
        }
      }
      
      setEditingCell(null);
      setEditingValue('');
      setOriginalEditValue('');
    } catch (error) {
      console.error('Error updating founder:', error);
      alert('Failed to save. Please try again.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Allow copy/paste commands to work - don't stop propagation for Cmd/Ctrl combinations
    const isCopyPaste = (e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'v' || e.key === 'x' || e.key === 'a');

    // Check if it's a navigation key (Tab or arrow keys)
    const isNavigationKey = e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';

    // For Tab and arrow keys: save edit, exit edit mode, and let event bubble to table navigation
    if (isNavigationKey) {
      handleSaveEdit();
      // Don't stop propagation - let the event bubble up to DataTable's keyboard handler
      return;
    }

    // Prevent bubbling to table-level handlers (swallow all except Enter/Escape, copy/paste, and navigation)
    if (!isCopyPaste && e.key !== 'Enter' && e.key !== 'Escape') {
      e.stopPropagation();
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleSaveEdit();
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setEditingCell(null);
      setEditingValue('');
      setOriginalEditValue('');
    }
  };

  // Parse date input in various formats
  const parseDateInput = (dateString: string): Date | null => {
    if (!dateString || !dateString.trim()) return null;
    
    const dateStr = dateString.trim();
    
    // Try ISO format first
    const isoDate = new Date(dateStr);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }
    
    // Try M/D/YY or M/D/YYYY format (e.g., "9/26/25" or "9/26/2025")
    const slashMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (slashMatch) {
      const month = parseInt(slashMatch[1], 10) - 1;
      const day = parseInt(slashMatch[2], 10);
      let year = parseInt(slashMatch[3], 10);
      if (year < 100) {
        year = year < 50 ? 2000 + year : 1900 + year;
      }
      if (month >= 0 && month < 12 && day >= 1 && day <= 31) {
        const date = new Date(year, month, day);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    
    return null;
  };

  const getCustomFieldValue = (founder: DisplayFounder, fieldId: string): string => {
    const customFieldValue = founder.customFieldValues?.find(cfv => cfv.fieldId === fieldId);
    return customFieldValue?.value || '';
  };

  const getCustomFieldDefinition = (fieldId: string): CustomFieldDefinition | undefined => {
    return effectiveCustomFields.find(field => field.id === fieldId);
  };

  const renderCustomFieldCell = (founder: DisplayFounder, fieldId: string) => {
    const field = getCustomFieldDefinition(fieldId);
    // Only show editing state if it's for this specific founder and field
    const isEditing = editingCustomField?.founderId === founder.id && editingCustomField?.fieldId === fieldId;
    let value = getCustomFieldValue(founder, fieldId);

    // Debug logging for multiselect issue
    if (isEditing) {
      console.log('[renderCustomFieldCell] EDITING MODE:', {
        fieldId,
        field: field ? { id: field.id, name: field.name, type: field.type, options: field.options } : 'UNDEFINED',
        effectiveCustomFieldsCount: effectiveCustomFields.length,
        effectiveCustomFieldIds: effectiveCustomFields.map(f => ({ id: f.id, name: f.name, type: f.type })),
        isEditing,
        editingValue,
      });
    }

    // For status field, if no custom field value exists but built-in status exists, use built-in status
    if (!value && field && (field.label?.toLowerCase() === 'status' || field.name?.toLowerCase() === 'status') && founder.status) {
      value = founder.status;
    }

    // Show editing input when in edit mode
    if (isEditing) {
      // Normalize type check to handle variations like 'multi_select', 'multiSelect', 'Multi Select', etc.
      const fieldType = field?.type?.toLowerCase().replace(/[_\-\s]/g, '');
      console.log('[renderCustomFieldCell] field.type RAW:', field?.type, 'normalized:', fieldType);

      // For select fields, show a dropdown
      if (fieldType === 'select') {
        const options = field?.options || [];
        return (
          <select
            value={editingValue}
            onChange={(e) => {
              setEditingValue(e.target.value);
              handleCustomFieldEdit(founder.id, fieldId, e.target.value);
            }}
            onBlur={() => {
              setEditingCustomField(null);
              setEditingValue('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                setEditingCustomField(null);
                setEditingValue('');
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            className="edit-input edit-select"
            autoFocus
          >
            <option value="">-- Select --</option>
            {options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );
      }

      // For multiselect fields, show Notion-style editor
      if (fieldType === 'multiselect') {
        const options = field?.options?.filter(opt => !opt.startsWith('__notion_type:')) || [];
        console.log('[renderCustomFieldCell] MULTISELECT - rendering MultiselectEditor with options:', options);
        // Parse current values - handle both JSON array and comma-separated formats
        let currentValues: string[] = [];
        try {
          const parsed = JSON.parse(editingValue);
          if (Array.isArray(parsed)) currentValues = parsed;
        } catch {
          currentValues = editingValue ? editingValue.split(',').map(v => v.trim()).filter(Boolean) : [];
        }
        console.log('[renderCustomFieldCell] MULTISELECT - currentValues:', currentValues);

        return createPortal(
          <div
            className="editor-portal-wrapper"
            style={editorPosition ? { top: editorPosition.top, left: editorPosition.left } : undefined}
          >
            <MultiselectEditor
              value={currentValues}
              options={options}
              onChange={(values) => handleCustomFieldEditMultiselect(founder.id, fieldId, values)}
              onClose={() => {
                setEditingCustomField(null);
                setEditingValue('');
                setEditorPosition(null);
              }}
              allowCreate={true}
            />
          </div>,
          document.body
        );
      }

      // For date fields, show calendar picker
      if (fieldType === 'date') {
        return createPortal(
          <div
            className="editor-portal-wrapper"
            style={editorPosition ? { top: editorPosition.top, left: editorPosition.left } : undefined}
          >
            <DateEditor
              value={editingValue}
              onChange={(date) => handleCustomFieldEdit(founder.id, fieldId, date)}
              onClose={() => {
                setEditingCustomField(null);
                setEditingValue('');
                setEditorPosition(null);
              }}
            />
          </div>,
          document.body
        );
      }

      // For person fields, show person selector
      if (fieldType === 'person') {
        const options = field?.options?.filter(opt => !opt.startsWith('__notion_type:')) || [];
        // Parse current values - handle both JSON array and string formats
        let currentValues: string[] = [];
        try {
          const parsed = JSON.parse(editingValue);
          if (Array.isArray(parsed)) {
            // Extract names from person objects if needed
            currentValues = parsed.map((p: any) => typeof p === 'string' ? p : p.name || '').filter(Boolean);
          } else if (typeof parsed === 'object' && parsed.name) {
            currentValues = [parsed.name];
          }
        } catch {
          currentValues = editingValue ? [editingValue] : [];
        }

        return createPortal(
          <div
            className="editor-portal-wrapper"
            style={editorPosition ? { top: editorPosition.top, left: editorPosition.left } : undefined}
          >
            <PersonEditor
              value={currentValues}
              options={options}
              onChange={(values) => handleCustomFieldEdit(founder.id, fieldId, JSON.stringify(values))}
              onClose={() => {
                setEditingCustomField(null);
                setEditingValue('');
                setEditorPosition(null);
              }}
              multiple={true}
            />
          </div>,
          document.body
        );
      }

      // Default: text/number input
      return (
        <input
          ref={editingInputRef}
          type={field?.type === 'number' ? 'number' : field?.type === 'date' ? 'date' : 'text'}
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() => handleCustomFieldEdit(founder.id, fieldId, editingValue)}
          onKeyDown={(e) => {
            // Check if it's a navigation key (Tab or arrow keys)
            const isNavigationKey = e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';

            // For Tab and arrow keys: save edit, exit edit mode, and let event bubble to table navigation
            if (isNavigationKey) {
              handleCustomFieldEdit(founder.id, fieldId, editingValue);
              return;
            }

            // Prevent bubbling to table-level handlers
            if (e.key !== 'Enter' && e.key !== 'Escape') {
              e.stopPropagation();
            }

            if (e.key === 'Enter' || e.key === 'NumpadEnter') {
              e.preventDefault();
              e.stopPropagation();
              handleCustomFieldEdit(founder.id, fieldId, editingValue);
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              e.stopPropagation();
              setEditingCustomField(null);
              setEditingValue('');
            }
          }}
          className="edit-input"
        />
      );
    }

    if (!field) return null;

    // Handle starting edit mode
    const handleStartEditing = () => {
      if (effectiveIsTableLocked) return;
      setEditingCustomField({ founderId: founder.id, fieldId });
      setEditingValue(value);
    };

    // Handle direct value change (for boolean toggle)
    const handleValueChange = async (newValue: string) => {
      await handleCustomFieldEdit(founder.id, fieldId, newValue);
    };

    // Check if this is a people field from Notion (special handling)
    const isPeopleField = field.options?.includes('__notion_type:people') || false;

    // If it's a Notion people field but type isn't 'person', render as person
    let effectiveType = field.type;
    if (isPeopleField && field.type === 'text') {
      effectiveType = 'person';
    }

    // Create a field definition with the effective type
    const effectiveFieldDefinition = {
      ...field,
      type: effectiveType,
    };

    // Use FieldDisplay for rendering
    return (
      <FieldDisplay
        value={value}
        fieldDefinition={effectiveFieldDefinition}
        isEditing={false}
        onStartEditing={handleStartEditing}
        onValueChange={handleValueChange}
        isReadOnly={effectiveIsTableLocked}
      />
    );
  };

  const handleCustomFieldEdit = async (founderId: string, fieldId: string, value: string) => {
    // Store previous values for rollback
    const previousValues = founder.customFieldValues || [];

    // Optimistic update: update local state immediately
    const existingEntry = previousValues.find(cfv => cfv.fieldId === fieldId);
    let updatedValues: CustomFieldValue[];
    if (existingEntry) {
      updatedValues = previousValues.map(cfv =>
        cfv.fieldId === fieldId ? { ...cfv, value } : cfv
      );
    } else {
      // Create a minimal CustomFieldValue for display purposes
      updatedValues = [...previousValues, {
        id: `temp-${Date.now()}`,
        fieldId,
        value,
        founderId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        field: {} as CustomFieldDefinition, // Will be populated on next fetch
      }];
    }

    // Apply optimistic update via parent callback
    if (onFounderUpdate) {
      onFounderUpdate(founderId, { customFieldValues: updatedValues });
    }

    // Close editing state
    setEditingCustomField(null);
    setEditingValue('');
    setEditorPosition(null);

    try {
      // Save to API
      await apiService.setCustomFieldValue(fieldId, founderId, value);
    } catch (error) {
      console.error('Error updating custom field:', error);
      // Rollback on error
      if (onFounderUpdate) {
        onFounderUpdate(founderId, { customFieldValues: previousValues });
      }
    }
  };

  // Special handler for multiselect fields that sends array instead of string
  const handleCustomFieldEditMultiselect = async (founderId: string, fieldId: string, values: string[]) => {
    // Store previous values for rollback
    const previousValues = founder.customFieldValues || [];

    // For display purposes, store as JSON string
    const displayValue = JSON.stringify(values);

    // Optimistic update: update local state immediately
    const existingEntry = previousValues.find(cfv => cfv.fieldId === fieldId);
    let updatedValues: CustomFieldValue[];
    if (existingEntry) {
      updatedValues = previousValues.map(cfv =>
        cfv.fieldId === fieldId ? { ...cfv, value: displayValue } : cfv
      );
    } else {
      // Create a minimal CustomFieldValue for display purposes
      updatedValues = [...previousValues, {
        id: `temp-${Date.now()}`,
        fieldId,
        value: displayValue,
        founderId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        field: {} as CustomFieldDefinition, // Will be populated on next fetch
      }];
    }

    // Apply optimistic update via parent callback
    if (onFounderUpdate) {
      onFounderUpdate(founderId, { customFieldValues: updatedValues });
    }

    // Close editing state
    setEditingCustomField(null);
    setEditingValue('');
    setEditorPosition(null);

    try {
      // Save to API - send array directly (backend expects array for multiselect)
      await apiService.setCustomFieldValue(fieldId, founderId, values);
      
      // After saving, check if we need to refresh custom fields (if new options were added)
      const field = getCustomFieldDefinition(fieldId);
      if (field) {
        const currentOptions = field.options || [];
        const hasNewOptions = values.some(v => !currentOptions.includes(v));
        
        if (hasNewOptions) {
          // Fetch updated custom fields to refresh the options list
          // This ensures new options appear in dropdowns
          const updatedFields = await apiService.getCustomFields();
          // The updated fields will be available on next render when customFields prop updates
          // We need to trigger a refresh of the customFields in useDataTable
          // Dispatch a custom event that useDataTable can listen to
          window.dispatchEvent(new CustomEvent('customFieldsUpdated'));
        }
      }
    } catch (error) {
      console.error('Error updating multiselect custom field:', error);
      // Rollback on error
      if (onFounderUpdate) {
        onFounderUpdate(founderId, { customFieldValues: previousValues });
      }
    }
  };

  const ensureTagField = async (): Promise<string | null> => {
    const match = (f: CustomFieldDefinition) => {
      const n = (f.name || '').toLowerCase();
      const l = (f.label || '').toLowerCase();
      return n === 'tag' || n === 'tags' || l === 'tag' || l === 'tags';
    };

    // 1) Check current in-memory list
    const existing = effectiveCustomFields.find(match);
    if (existing) return existing.id;

    // 2) Fetch latest from API (in case another tab/user created it)
    try {
      const latest = await apiService.getCustomFields();
      const found = latest.find(match);
      if (found) return found.id;
    } catch (_) {
      // ignore fetch errors here; we'll attempt create next
    }

    // 3) Create if still not present
    try {
      const created = await apiService.createCustomField({
        name: 'tag',
        label: 'Tag',
        type: 'text',
        required: false,
      });
      return created.id;
    } catch (e: any) {
      // If server says it already exists, fetch again and return its id
      const message = (e && (e.message || e.toString())) as string;
      if (message && /already exists/i.test(message)) {
        try {
          const latest = await apiService.getCustomFields();
          const found = latest.find(match);
          if (found) return found.id;
        } catch (_) {}
      }
      console.error('Failed to create Tag field', e);
      return null;
    }
  };

  const ensureStatusField = async (): Promise<string | null> => {
    const match = (f: CustomFieldDefinition) => {
      const n = (f.name || '').toLowerCase();
      const l = (f.label || '').toLowerCase();
      return n === 'status' || l === 'status';
    };

    // 1) Check current in-memory list
    const existing = effectiveCustomFields.find(match);
    if (existing) return existing.id;

    // 2) Fetch latest from API (in case another tab/user created it)
    try {
      const latest = await apiService.getCustomFields();
      const found = latest.find(match);
      if (found) {
        // Only refresh if the field wasn't in the current list
        const alreadyInList = customFields.some(f => f.id === found.id);
        if (!alreadyInList && onRefresh) {
          onRefresh();
        }
        return found.id;
      }
    } catch (_) {
      // ignore fetch errors here; we'll attempt create next
    }

    // 3) Create if still not present
    try {
      const created = await apiService.createCustomField({
        name: 'status',
        label: 'Status',
        type: 'text',
        required: false,
      });
      // Refresh custom fields list after creation only if it's a new field
      if (created && !customFields.some(f => f.id === created.id) && onRefresh) {
        onRefresh();
      }
      return created.id;
    } catch (e: any) {
      // If server says it already exists (409 Conflict), fetch again and return its id
      const statusCode = e?.status || e?.response?.status;
      const message = (e && (e.message || e.toString())) as string;
      if (statusCode === 409 || (message && /already exists|conflict/i.test(message))) {
        try {
      const latest = await apiService.getCustomFields();
      const found = latest.find(match);
      if (found) {
        // Only refresh if the field wasn't in the current list
        const alreadyInList = customFields.some(f => f.id === found.id);
        if (!alreadyInList && onRefresh) {
          onRefresh();
        }
        return found.id;
      }
        } catch (_) {}
      }
      console.error('Failed to create Status field', e);
      return null;
    }
  };

  const handleDeleteFounder = async () => {
    if (!window.confirm(`Are you sure you want to permanently delete ${founder.name}? This action cannot be undone.`)) {
      return;
    }

    try {
      // Use onDeleteFounder if provided (database deletion), otherwise fall back to database deletion
      if (onDeleteFounder) {
        await onDeleteFounder(founder.id);
      } else {
        await apiService.deleteFounder(founder.id);
        // Refresh the data to remove the deleted founder from the list
        if (onRefresh) {
          onRefresh();
        }
      }
    } catch (error) {
      console.error('Error deleting founder:', error);
      alert('Failed to delete person. Please try again.');
    }
  };

  const renderBuiltInFieldCell = (founder: DisplayFounder, fieldId: string) => {
    const fieldRenderers = {
      stage: () => founder.stage ? <div className="pill stage-pill"><span>{founder.stage}</span></div> : null,
      followerCount: () => founder.followerCount ? <span className="follower-count">{founder.followerCount.toLocaleString()}</span> : null,
      priorityScore: () => founder.priorityScore ? <span className="priority-score">{founder.priorityScore}</span> : null,
      status: () => founder.status ? <div className="pill status-pill"><span>{founder.status}</span></div> : null,
      owner: () => founder.owner ? (
        <div className="team-cell">
          <div className="team-avatar" style={{ backgroundColor: '#10b981' }}>
            {getInitials(founder.owner)}
          </div>
          <span className="team-text">{founder.owner}</span>
        </div>
      ) : null,
      lastTouchAt: () => founder.lastTouchAt ? <span className="date-text">{formatUpdateTime(founder.lastTouchAt)}</span> : null
    };

    return fieldRenderers[fieldId as keyof typeof fieldRenderers]?.() || null;
  };

  const renderCell = (columnId: string) => {
    // Add cell selection handlers to all <td> elements
    const cellClickHandler = () => {
      console.log('[TableRow] cellClickHandler called:', { rowIndex, columnId, founderName: founder.name });
      onCellClick?.(rowIndex || 0, columnId);
    };
    const cellMouseDownHandler = () => {
      console.log('[TableRow] cellMouseDownHandler called:', { rowIndex, columnId, founderName: founder.name });
      onCellMouseDown?.(rowIndex || 0, columnId);
    };
    const cellMouseMoveHandler = () => {
      onCellMouseMove?.(rowIndex || 0, columnId);
    };
    
    // Determine if this cell is active or selected
    const isActive = effectiveActiveCell && effectiveActiveCell.row === (rowIndex ?? -1) && effectiveActiveCell.colKey === columnId;
    const isSelected = effectiveSelectionRect &&
      (rowIndex ?? -1) >= effectiveSelectionRect.r0 &&
      (rowIndex ?? -1) <= effectiveSelectionRect.r1 &&
      effectiveSelectionRect.colOrder.indexOf(columnId) >= effectiveSelectionRect.c0 &&
      effectiveSelectionRect.colOrder.indexOf(columnId) <= effectiveSelectionRect.c1;
    
    // Build className
    const cellClasses = `${
      isActive ? 'cell-active' : ''
    } ${
      isSelected ? 'cell-selected' : ''
    }`.trim();

    if (columnId === 'name') {
      const isEditing = editingCell?.founderId === founder.id && editingCell?.field === 'name';
      const isNewRow = founder.id?.startsWith('new-');
      
      return (
        <td key={columnId} className={`name-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {isEditing ? (
            <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div className="name-cell">
              <span 
                className={`name-text ${isNewRow ? 'editable' : 'clickable'}`}
                onClick={(e) => {
                  if (isNewRow) {
                    // For new rows, start editing on click
                    handleCellDoubleClick(founder.id, 'name', founder.name || '');
                  } else {
                    // Delay navigation to allow double-click to trigger first
                    const clickTimer = setTimeout(() => {
                      onFounderClick?.(founder.id);
                    }, 200); // 200ms delay to detect double-click
                    
                    // Store timer in element data to clear it on double-click
                    (e.currentTarget as any).clickTimer = clickTimer;
                  }
                }}
                onDoubleClick={(e) => {
                  // Clear the single-click timer if it exists
                  if ((e.currentTarget as any).clickTimer) {
                    clearTimeout((e.currentTarget as any).clickTimer);
                    delete (e.currentTarget as any).clickTimer;
                  }
                  
                  if (!isNewRow) {
                    // Prevent navigation and start editing on double-click
                    e.stopPropagation();
                    handleCellDoubleClick(founder.id, 'name', founder.name || '');
                  }
                }}
              >
                {founder.name || ''}
              </span>
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'email') {
      return (
        <td key={columnId} className={`email-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'email' ? (
            <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill email-pill editable ${!founder.email ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'email', founder.email || '')}
            >
{founder.email || ''}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'company') {
      return (
        <td key={columnId} className={`company-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'company' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill company-pill editable ${!founder.company ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'company', founder.company || '')}
            >
{founder.company || ''}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'position') {
      return (
        <td key={columnId} className={`position-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'position' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill company-pill editable ${!founder.roleTitle ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'position', founder.roleTitle || '')}
            >
{founder.roleTitle || ''}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'linkedinUrl') {
      return (
        <td key={columnId} className={`linkedin-url-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'linkedinUrl' ? (
            <input
              ref={editingInputRef}
              type="url"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
              placeholder="https://linkedin.com/in/username"
            />
          ) : (
            <div 
              className="linkedin-cell editable"
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'linkedinUrl', founder.profileUrl || '')}
            >
              {founder.profileUrl ? (
                <a 
                  href={founder.profileUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="linkedin-url-link"
                  onClick={(e) => e.stopPropagation()}
                  title={founder.profileUrl}
                >
                  {founder.profileUrl}
                </a>
              ) : (
<span className="linkedin-placeholder"></span>
              )}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'industry') {
      return (
        <td key={columnId} className={`industry-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'industry' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill company-pill editable ${!founder.industry ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'industry', founder.industry || '')}
            >
{founder.industry || ''}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'location') {
      return (
        <td key={columnId} className={`location-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'location' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill company-pill editable ${!founder.location ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'location', founder.location || '')}
            >
{founder.location || ''}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'status') {
      return (
        <td key={columnId} className={`status-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'status' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill company-pill editable ${!founder.status ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'status', founder.status || '')}
            >
{founder.status || ''}
            </div>
          )}
        </td>
      );
    }

    if (columnId === 'tag') {
      // Generate consistent pastel color for each tag
      const getTagColor = (tag: string) => {
        if (!tag) return { bg: 'transparent', text: '#6b7280' };

        const tagColors: Record<string, { bg: string; text: string }> = {
          'sellers': { bg: 'rgba(186, 230, 253, 0.5)', text: '#0369a1' }, // light blue
          'friends': { bg: 'rgba(221, 214, 254, 0.5)', text: '#6d28d9' }, // lavender
          'friend': { bg: 'rgba(221, 214, 254, 0.5)', text: '#6d28d9' }, // lavender
          'investor': { bg: 'rgba(167, 243, 208, 0.5)', text: '#047857' }, // light green
          'partner': { bg: 'rgba(254, 215, 170, 0.5)', text: '#c2410c' }, // light orange
          'customer': { bg: 'rgba(252, 231, 243, 0.5)', text: '#be185d' }, // light pink
          'prospect': { bg: 'rgba(254, 249, 195, 0.5)', text: '#a16207' }, // light yellow
        };

        // If tag exists in predefined colors, use it
        if (tagColors[tag.toLowerCase()]) {
          return tagColors[tag.toLowerCase()];
        }

        // Generate a consistent color based on tag string
        let hash = 0;
        for (let i = 0; i < tag.length; i++) {
          hash = tag.charCodeAt(i) + ((hash << 5) - hash);
        }

        const pastelColors = [
          { bg: 'rgba(186, 230, 253, 0.5)', text: '#0369a1' }, // blue
          { bg: 'rgba(221, 214, 254, 0.5)', text: '#6d28d9' }, // purple
          { bg: 'rgba(167, 243, 208, 0.5)', text: '#047857' }, // green
          { bg: 'rgba(254, 215, 170, 0.5)', text: '#c2410c' }, // orange
          { bg: 'rgba(252, 231, 243, 0.5)', text: '#be185d' }, // pink
          { bg: 'rgba(254, 249, 195, 0.5)', text: '#a16207' }, // yellow
          { bg: 'rgba(207, 250, 254, 0.5)', text: '#0e7490' }, // cyan
          { bg: 'rgba(254, 202, 202, 0.5)', text: '#b91c1c' }, // red
        ];

        return pastelColors[Math.abs(hash) % pastelColors.length];
      };

      const tagColor = getTagColor(founder.tag || '');

      return (
        <td key={columnId} className={`tag-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'tag' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div
              className={`tag-pill-container ${!founder.tag ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'tag', founder.tag || '')}
            >
              {founder.tag && (
                <span
                  className="tag-pill"
                  style={{
                    backgroundColor: tagColor.bg,
                    color: tagColor.text
                  }}
                >
                  {founder.tag}
                </span>
              )}
            </div>
          )}
        </td>
      );
    }

    // Handle stage column separately with click editing
    if (columnId === 'stage') {
      const stageValue = founder.stage || '';
      return (
        <td key={columnId} className={`stage-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {editingCell?.founderId === founder.id && editingCell?.field === 'stage' ? (
              <input
              ref={editingInputRef}
              type="text"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              onBlur={handleSaveEdit}
              onKeyDown={handleKeyPress}
              className="edit-input"
            />
          ) : (
            <div 
              className={`pill company-pill editable ${!stageValue ? 'empty-placeholder' : ''}`}
              onDoubleClick={() => handleCellDoubleClick(founder.id, 'stage', stageValue)}
            >
{stageValue || ''}
            </div>
          )}
        </td>
      );
    }

    // Handle profileUrl column (separate from linkedinUrl column)
    if (columnId === 'profileUrl') {
      return (
        <td key={columnId} className={`profile-url-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          <div 
            className="linkedin-cell editable"
            onDoubleClick={() => handleCellDoubleClick(founder.id, 'linkedinUrl', founder.profileUrl || '')}
          >
            {founder.profileUrl ? (
              <a 
                href={founder.profileUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="linkedin-url-link"
                onClick={(e) => e.stopPropagation()}
                title={founder.profileUrl}
              >
                {founder.profileUrl}
              </a>
            ) : (
              <span className="linkedin-placeholder"></span>
            )}
          </div>
        </td>
      );
    }

    // Handle lastPostExcerpt column
    if (columnId === 'lastPostExcerpt') {
      const excerpt = founder.lastPostExcerpt || '';
      return (
        <td key={columnId} className={`last-post-excerpt-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          <div className="pill company-pill" title={excerpt}>
            {excerpt || <span className="empty-placeholder"></span>}
          </div>
        </td>
      );
    }

    // Handle lastPostDate column
    if (columnId === 'lastPostDate') {
      const postDate = founder.lastPostDate;
      return (
        <td key={columnId} className={`last-post-date-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {postDate ? (
            <span className="date-text">{formatUpdateTime(postDate)}</span>
          ) : (
            <span className="empty-placeholder"></span>
          )}
        </td>
      );
    }

    // Handle nextAction column
    if (columnId === 'nextAction') {
      const nextAction = founder.nextAction || '';
      return (
        <td key={columnId} className={`next-action-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          <div className="pill company-pill" title={nextAction}>
            {nextAction || <span className="empty-placeholder"></span>}
          </div>
        </td>
      );
    }

    // Handle additional built-in fields that don't have special rendering
    const builtInColumns = ['followerCount', 'priorityScore', 'owner', 'lastTouchAt', 'accelerator', 'contactPhone', 'lastEditedBy', 'type', 'vcFirm', 'xUrl', 'substackUrl'];
    if (builtInColumns.includes(columnId)) {
      // For simple text fields, render them as editable cells
      if (['accelerator', 'contactPhone', 'lastEditedBy', 'type', 'vcFirm', 'xUrl', 'substackUrl'].includes(columnId)) {
        const fieldMap: Record<string, 'accelerator' | 'contactPhone' | 'lastEditedBy' | 'type' | 'vcFirm' | 'xUrl' | 'substackUrl'> = {
          accelerator: 'accelerator',
          contactPhone: 'contactPhone',
          lastEditedBy: 'lastEditedBy',
          type: 'type',
          vcFirm: 'vcFirm',
          xUrl: 'xUrl',
          substackUrl: 'substackUrl',
        };
        const field: 'accelerator' | 'contactPhone' | 'lastEditedBy' | 'type' | 'vcFirm' | 'xUrl' | 'substackUrl' | undefined = fieldMap[columnId];
        
        if (!field) {
          return (
            <td key={columnId} className={`${columnId}-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
              {renderBuiltInFieldCell(founder, columnId)}
            </td>
          );
        }
        
        const isEditing = editingCell?.founderId === founder.id && editingCell?.field === field;
        const value = (founder as any)[columnId] || '';
        
        return (
          <td key={columnId} className={`${columnId}-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
            {isEditing ? (
              <input
                ref={editingInputRef}
                type="text"
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={handleSaveEdit}
                onKeyDown={handleKeyPress}
                className="edit-input"
              />
            ) : (
              <div 
                className={`pill company-pill editable ${!value ? 'empty-placeholder' : ''}`}
                onDoubleClick={() => handleCellDoubleClick(founder.id, field, value)}
              >
                {value || ''}
              </div>
            )}
          </td>
        );
      }
      
      // For other built-in columns, use the renderer
      return (
        <td key={columnId} className={`${columnId}-column ${cellClasses}`} onClick={cellClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {renderBuiltInFieldCell(founder, columnId)}
        </td>
      );
    }

    const field = getCustomFieldDefinition(columnId);
    if (field) {
      // Custom click handler for custom fields that triggers editing
      const customFieldClickHandler = (e: React.MouseEvent<HTMLTableCellElement>) => {
        cellClickHandler();
        // Also trigger editing if not already editing and table isn't locked
        if (!effectiveIsTableLocked && !editingCustomField) {
          const value = getCustomFieldValue(founder, columnId);
          // Capture cell position for portal-based editors
          const rect = e.currentTarget.getBoundingClientRect();
          setEditorPosition({ top: rect.bottom, left: rect.left });
          setEditingCustomField({ founderId: founder.id, fieldId: columnId });
          setEditingValue(value);
        }
      };
      return (
        <td key={columnId} className={`custom-field-column ${cellClasses}`} onClick={customFieldClickHandler} onMouseDown={cellMouseDownHandler} onMouseMove={cellMouseMoveHandler}>
          {renderCustomFieldCell(founder, columnId)}
        </td>
      );
    }

    return null;
  };

  return (
    <tr className={`table-row ${founder.selected ? 'selected' : ''}`}>
      <td className="checkbox-column">
        <div className="checkbox-container">
          <input
            type="checkbox"
            checked={founder.selected || false}
            onChange={(e) => onToggleSelect?.(founder.id, e.target.checked)}
            className="row-checkbox"
          />
        </div>
      </td>
      {visibleColumns.includes('no') && (
        <td className="no-column">
          <div className="row-number">
            {rowNumber || ''}
          </div>
        </td>
      )}
      {visibleColumns.includes('name') && renderCell('name')}
      {visibleColumns.includes('email') && renderCell('email')}
      {visibleColumns
        .filter(col => col !== 'no' && col !== 'update' && col !== 'name' && col !== 'email')
        .map(renderCell)
        .filter(cell => cell !== null)}
      {visibleColumns.includes('update') && (
      <td className="update-column">
        <div className="update-column-content">
          <div className="update-column-main-content">
            {loadingAISummary || refreshingSummary ? (
              <div className="loading-summary">
                <span className="loading-dots">Loading</span>
              </div>
            ) : latestAISummary ? (
              <div className="ai-summary">
                <div className="ai-summary-text">
                  {latestAISummary}{' '}
                  <button 
                    className="posts-button-inline"
                    onClick={handlePostsClick}
                    title="View most recent LinkedIn post"
                  >
                    <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            ) : founder.lastPostSummary ? (
              <div className="ai-summary">
                <div className="ai-summary-text">
                  {founder.lastPostSummary}{' '}
                  <button 
                    className="posts-button-inline"
                    onClick={handlePostsClick}
                    title="View most recent LinkedIn post"
                  >
                    <ExternalLink size={12} />
                  </button>
                </div>
              </div>
            ) : (
              <span className="no-summary">No recent posts</span>
            )}
            <button 
              className={`refresh-button-inline ${refreshingSummary ? 'refreshing' : ''}`}
              onClick={handleRefreshClick}
              disabled={refreshingSummary || hasActivePhantomJobs}
              title={hasActivePhantomJobs ? 'Refresh in progress' : 'Refresh LinkedIn updates'}
            >
              <RefreshCw size={14} />
            </button>
          </div>
          {(latestAISummary || founder.lastPostSummary) && (
            <div className="timestamp-container">
              <div className="last-refreshed-time">
                Updated {formatLastRefreshed(latestAISummary ? (lastUpdatedAt || undefined) : founder.lastPostSummaryDate)}
              </div>
              {(latestPostTimestamp || founder.lastPostDate) && (
                <div className="post-date-time">
                  Posted {formatPostDate(latestPostTimestamp || founder.lastPostDate)}
                </div>
              )}
            </div>
          )}
        </div>
      </td>
      )}
      
      {/* Refresh Confirmation Modal - Using Portal to render outside table */}
      {showRefreshModal && createPortal(
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Refresh LinkedIn Profile</h3>
            </div>
            <div className="modal-body">
              <p>Refreshing LinkedIn profile for <strong>{founder.name}</strong></p>
              <p>This will fetch the latest posts and update AI summaries.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="modal-button modal-button-cancel" 
                onClick={handleCancelRefresh}
              >
                Cancel
              </button>
              <button 
                className="modal-button modal-button-confirm" 
                onClick={handleConfirmRefresh}
              >
                OK
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </tr>
  );
};

export default TableRow;
